import { FC, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useListChats } from "~/api/chat/chat";
import { ChatListItem } from "~/api/model";
import { Button } from "~/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "~/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { formatDate, ROUTES, useChatDocumentParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { cn } from "~/lib/utils";

const UNTITLED_CHAT_FALLBACK = "Untitled Chat";

const formatChatLabel = (chat: ChatListItem): string => {
  return chat.title ?? UNTITLED_CHAT_FALLBACK;
};

const ChatItemMetadata: FC<{ chat: ChatListItem }> = ({ chat }) => {
  const { formatMessage } = useIntl();
  return (
    <span className="text-xs text-muted-foreground whitespace-nowrap">
      {chat.messageCount > 0 &&
        formatMessage(
          { id: "chat.history.messageCount" },
          { count: chat.messageCount }
        )}
      {chat.messageCount > 0 && " · "}
      {formatDate(chat.updatedAt)}
    </span>
  );
};

export const ChatHistorySelector: FC = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { organizationId, caseId, chatId } = useChatDocumentParams();
  const { isMobile, isLoading } = useMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data } = useListChats(organizationId, caseId);

  const chatList = useMemo(() => data?.chatList ?? [], [data?.chatList]);

  if (isLoading || chatList.length <= 1) {
    return null;
  }

  const handleChatSelect = (selectedChatId: string) => {
    if (selectedChatId === chatId) return;
    navigate(
      ROUTES.CHAT_DOCUMENT({
        organizationId,
        caseId,
        chatId: selectedChatId,
      })
    );
  };

  const currentChat = chatList.find((c) => c.id === chatId);

  if (isMobile) {
    return (
      <div className="px-3 pt-3 pb-1">
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <Button
            variant="outline"
            className="w-full justify-between h-9 px-3 text-sm font-normal"
            onClick={() => setDrawerOpen(true)}
          >
            <span className="truncate min-w-0">
              {currentChat
                ? formatChatLabel(currentChat)
                : formatMessage({ id: "chat.history.placeholder" })}
            </span>
            {currentChat && <ChatItemMetadata chat={currentChat} />}
          </Button>
          <DrawerContent className="pb-safe-bottom">
            <DrawerHeader>
              <DrawerTitle>
                {formatMessage({ id: "chat.history.title" })}
              </DrawerTitle>
            </DrawerHeader>
            <div className="flex flex-col gap-1 px-4 pb-6 max-h-[60dvh] overflow-y-auto">
              {chatList.map((chat) => {
                const isSelected = chat.id === chatId;
                return (
                  <Button
                    key={chat.id}
                    variant={isSelected ? "secondary" : "ghost"}
                    className="w-full justify-between h-auto min-h-[44px] px-3 py-2"
                    onClick={() => {
                      handleChatSelect(chat.id);
                      setDrawerOpen(false);
                    }}
                  >
                    <span
                      className={cn(
                        "flex-1 truncate text-left text-sm",
                        isSelected && "font-semibold"
                      )}
                    >
                      {formatChatLabel(chat)}
                    </span>
                    <ChatItemMetadata chat={chat} />
                  </Button>
                );
              })}
            </div>
          </DrawerContent>
        </Drawer>
      </div>
    );
  }

  return (
    <div className="px-3 pt-3 pb-1">
      <Select value={chatId} onValueChange={handleChatSelect}>
        <SelectTrigger className="w-full">
          <SelectValue
            placeholder={formatMessage({ id: "chat.history.placeholder" })}
          />
        </SelectTrigger>
        <SelectContent>
          {chatList.map((chat) => {
            const isSelected = chat.id === chatId;
            return (
              <SelectItem
                key={chat.id}
                value={chat.id}
                className="[&>span:first-child]:hidden"
              >
                <div className="w-full flex justify-between items-center gap-2">
                  <span
                    className={cn(
                      "flex-1 truncate",
                      isSelected && "font-semibold"
                    )}
                  >
                    {formatChatLabel(chat)}
                  </span>
                  <ChatItemMetadata chat={chat} />
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};
